export interface CampaignField {
  id: string;
  label: string;
  kind: "text" | "number" | "email" | "call" | "whatsapp" | "sms" | "multiple_choice" | "checkboxes" | "dropdown" | "date" | "time";
  required?: boolean;
  // Only meaningful for multiple_choice/checkboxes/dropdown.
  options?: string[];
}

// Checkboxes answers join their selected options into one string (the
// `values` dict is str-valued everywhere else) — must match
// CHECKBOX_DELIMITER in techladder-core/app/domain/crm/router.py.
export const CHECKBOX_DELIMITER = "||";

export interface LeadCampaign {
  id: string;
  name: string;
  createdAt?: string;
  updatedAt?: string;
  fields?: CampaignField[];
  token?: string;
  form_url?: string;
  schedule?: CampaignSchedule | null;
}

export interface Lead {
  assignedAgentId?: string | null;
  id: string;
  // Stable cross-system handle for this contact, assigned once at creation
  // and reused as-is by manual calls, call history, and workflow batch
  // uploads. Falls back to `id` for leads created before this existed.
  contactId?: string;
  // A campaign's Full-name/Phone questions can now be relabeled, made
  // optional, or removed entirely, so a lead may have neither.
  name?: string;
  phone?: string;
  email?: string;
  company?: string;
  campaignId: string;
  source?: string;
  // Set when the lead was created from a CRM contact batch upload rather
  // than a campaign form or the manual "Add lead" dialog.
  batchId?: string;
  batchName?: string;
  notes?: string;
  fresh: boolean;
  status?: string;
  createdAt: string;
  updatedAt?: string;
  // Who/what actually touched this lead outside the CRM, computed server-side
  // from call_summaries3 / notifications on every GET /crm/leads.
  lastCallAgentName?: string;
  workflowName?: string;
  sentChannels?: string[];
  // Most recent WhatsApp Campaign this lead was sent, if any.
  campaignName?: string;
  whatsappInstance?: string;
  values?: Record<string, string>;
}

export const LEAD_SOURCES = ["Website", "IndiaMART", "Referral", "Walk-in", "Social Media", "Other"] as const;

export const LEAD_STATUSES = ["Fresh lead", "Contacted", "Interested", "Follow-up", "Qualified", "Converted", "Not interested"];
export const leadStatus = (lead: Lead): string => lead.status || (lead.fresh ? "Fresh lead" : "Contacted");

// Reason-coded per-attempt call dispositions (Phase 2A). Must stay in sync
// with Interaction.outcome in techladder-core/app/domain/crm/workflows.py —
// anything else is rejected with a 422 on save.
export const CALL_OUTCOMES = ["Connected", "Interested", "Call Back", "Not Interested", "No Answer", "Busy", "Switched off", "Wrong number", "Disconnected", "Language issue", "Callback"] as const;
export type CallOutcome = (typeof CALL_OUTCOMES)[number];
// Default lead-status rollup per disposition. The disposition itself is stored
// on the attempt; this only seeds the status dropdown next to it.
export const CALL_OUTCOME_STATUS: Record<CallOutcome, string> = {
  Connected: "Contacted",
  Interested: "Interested",
  "Call Back": "Follow-up",
  "Not Interested": "Not interested",
  "No Answer": "Contacted",
  Busy: "Follow-up",
  "Switched off": "Contacted",
  "Wrong number": "Contacted",
  Disconnected: "Contacted",
  "Language issue": "Follow-up",
  Callback: "Follow-up",
};

const STATUS_PILL_CLS: Record<string, string> = {
  "Fresh lead": "bg-sky-100 text-sky-700",
  "Contacted": "bg-soft text-ink-2",
  "Interested": "bg-green-100 text-green-700",
  "Follow-up": "bg-amber-100 text-amber-700",
  "Qualified": "bg-purple-100 text-purple-700",
  "Converted": "bg-green-100 text-green-700",
  "Not interested": "bg-red-100 text-red-700",
};
// Shared so a status reads the same color everywhere it appears (leads
// table, agent drawer's assigned-leads list, ...).
export const statusPillCls = (status: string): string => STATUS_PILL_CLS[status] ?? "bg-soft text-ink-2";

export const initialsOf = (name?: string): string =>
  (name || "?").trim().split(/\s+/).filter(Boolean).map((part) => part[0]).slice(0, 2).join("").toUpperCase() || "?";

// A small fixed palette so each agent gets a stable, distinct avatar color
// (picked by hashing their id) instead of every avatar looking the same.
const AVATAR_PALETTE = [
  "bg-green-500", "bg-blue-500", "bg-amber-500", "bg-cyan-500", "bg-purple-500", "bg-pink-500", "bg-orange-500", "bg-teal-500",
];
export const avatarColorOf = (id: string): string => {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
};

// The one place every feature (calls, call history, workflow batch upload)
// should read a lead's contact id from — keeps them all pointed at the same
// value even for leads created before `contactId` existed.
export const contactIdOf = (lead: Lead): string => lead.contactId || lead.id;

export type CrmMember = { id: string; name: string; email: string; phone: string; role: string; active: boolean; leads?: number; calls?: number; interested?: number; presence?: string };

export type CrmRolePermissions = { view_all_leads: boolean; edit_leads: boolean; delete_leads: boolean; assign_leads: boolean };
export type CrmRole = { id: string; name: string; permissions: CrmRolePermissions; active: boolean; builtin?: boolean };
export const CRM_PERMISSION_LABELS: Record<keyof CrmRolePermissions, string> = {
  view_all_leads: "View all leads",
  edit_leads: "Edit leads",
  delete_leads: "Delete leads",
  assign_leads: "Assign leads",
};

export type CrmTeam = { role: string; userId: string; permissions?: CrmRolePermissions; members: CrmMember[] };
export type CrmInteraction = { id?: string; kind: string; outcome?: string; notes?: string; nextAction?: string; status?: string; callId?: string; createdAt?: string; actorName?: string; duration?: number; recording?: { available: boolean }; summary?: string; transcriptExcerpt?: string; classification?: string; followUp?: { dueAt: string; note: string; done?: boolean; assignedAgentId?: string | null } };
export type CrmFollowUp = { id: string; leadId: string; name: string; phone: string; company?: string; assignedAgentId?: string; dueAt: string; note: string; done: boolean; fresh: boolean };

// Appointment & demo booking (Phase 2B). Statuses match APPOINTMENT_STATUSES
// in techladder-core/app/domain/crm/workflows.py.
export const APPOINTMENT_STATUSES = ["Scheduled", "Completed", "No-show", "Rescheduled"] as const;
export type CrmAppointment = { id: string; leadId: string; name: string; phone: string; dueAt: string; note: string; status: string; createdAt: string };
export const APPOINTMENT_PILL_CLS: Record<string, string> = {
  Scheduled: "bg-sky-100 text-sky-700",
  Completed: "bg-green-100 text-green-700",
  "No-show": "bg-red-100 text-red-700",
  Rescheduled: "bg-amber-100 text-amber-700",
};
export const appointmentPillCls = (status: string): string => APPOINTMENT_PILL_CLS[status] ?? "bg-soft text-ink-2";

// Campaign management (Phase 2C). Schedule shapes match CampaignSchedule in
// techladder-core/app/domain/crm/router.py; absent schedule reads as always open.
export type CampaignWindow = { days: number[]; start: string; end: string };
export type CampaignSchedule = { timezone: string; active: boolean; startDate?: string | null; endDate?: string | null; windows: CampaignWindow[] };
export type CampaignMetrics = {
  id: string; name: string; leads: number; calls: number; connected: number;
  interested: number; converted: number; openFollowUps: number;
  openNow: boolean; schedule?: CampaignSchedule | null;
};
export const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Global notes view (Phase 2C). Rows match GET /crm/notes in
// techladder-core/app/domain/crm/workflows.py — one row per note interaction.
export type CrmNote = { id: string; leadId: string; leadName: string; notes: string; actorName: string; createdAt: string };

// Notes are stored as `Title\n\nBody` (title optional). First line renders as
// the card title, the rest as the preview body.
export const splitNoteTitle = (notes: string): [string, string] => {
  const lines = (notes || "").split("\n");
  const title = (lines[0] || "").trim();
  const body = lines.slice(1).join("\n").trim();
  if (!title) return [body.slice(0, 80) || "Untitled", ""];
  return [title, body];
};
// Duplicate detection review center (Phase 2C). Groups match the backend's
// phoneKey / emailKey buckets; the keeper absorbs the others' history.
export type DuplicateMember = { id: string; name: string; phone: string; email: string; company: string; createdAt: string; assignedAgentId?: string | null; notes: boolean; interactions: number };
export type DuplicateGroup = { key: string; field: "phone" | "email"; value: string; leads: DuplicateMember[] };

// Auto-dialer (Phase 2C). Queue items are snapshots; position is list order.
export type DialerQueueItem = { id: string; name: string; phone: string; company: string; status: string; createdAt: string };
export type DialerQueue = { campaignId: string; openNow: boolean; items: DialerQueueItem[] };
export type DialerState = { campaignId: string; status: "active" | "paused" | "stopped" };

// Notification center (Phase 2C). Triggers match TRIGGERS in
// techladder-core/app/domain/crm/notifications.py.
export const NOTIFICATION_TRIGGERS = ["new_lead", "reassignment", "callback", "appointment"] as const;
export type NotificationTrigger = (typeof NOTIFICATION_TRIGGERS)[number];
export const NOTIFICATION_TRIGGER_LABELS: Record<NotificationTrigger, string> = {
  new_lead: "New leads",
  reassignment: "Reassignments",
  callback: "Follow-up callbacks",
  appointment: "Appointments",
};
export type CrmNotification = { id: string; trigger: string; title: string; body: string; leadId?: string | null; read: boolean; createdAt: string };

// Agent call count & activity (Phase 2B). Ranges match the backend report
// presets ("day" | "week" | "month" | "custom").
export type AgentActivityDay = { date: string; total: number; connected: number };
export type AgentActivity = { id: string; name: string; totalCalls: number; connectedCalls: number; byDay: AgentActivityDay[] };

// Login & attendance (Phase 2B). One row per roster member per UTC day.
export type AttendanceRow = {
  id: string; name: string; email: string; role: string; active: boolean;
  firstSeenAt: string | null; lastSeenAt: string | null;
  sessions: number; activeMinutes: number;
  callMinutes: number; idleMinutes: number; utilization: number | null;
};

// Automated assignment rules (Phase 2A). Modes must match MODES in
// techladder-core/app/domain/crm/assignment.py. campaignId null = default.
export const ASSIGNMENT_MODES = ["round_robin", "source", "load_balanced"] as const;
export type AssignmentMode = (typeof ASSIGNMENT_MODES)[number];
export const ASSIGNMENT_MODE_LABELS: Record<AssignmentMode, string> = {
  round_robin: "Round robin",
  source: "By source",
  load_balanced: "Load balanced",
};
export const ASSIGNMENT_MODE_DESCRIPTIONS: Record<AssignmentMode, string> = {
  round_robin: "Deal new leads out evenly, one after another.",
  source: "Send each source to its agent; anything unmapped falls back to round robin.",
  load_balanced: "Always give the new lead to the agent with the fewest open leads.",
};
export type AssignmentRule = {
  id: string; campaignId: string | null; mode: AssignmentMode;
  agentIds: string[]; sourceMap: Record<string, string>;
  active: boolean; lastIndex: number; updatedAt: string; updatedBy: string;
};

// Lead aging & SLA alerts (Phase 2A). Thresholds live server-side; the breach
// reasons here must match sla.breach_reasons ("assignment" | "first_contact").
export type SlaAgentOverride = { assignment_hours?: number; first_contact_hours?: number };
export type SlaConfig = { enabled: boolean; assignment_hours: number; first_contact_hours: number; agents: Record<string, SlaAgentOverride>; updatedAt: string; updatedBy: string };
export type SlaBreach = { id: string; name: string; phone: string; assignedAgentId?: string | null; reasons: string[]; createdAt: string };
export const SLA_REASON_LABELS: Record<string, string> = { assignment: "Unassigned past SLA", first_contact: "No contact past SLA" };

// DND / calling-compliance suppression list (Phase 2A). Reasons must stay in
// sync with REASONS in techladder-core/app/domain/crm/suppression.py.
export const SUPPRESSION_REASONS = ["DND registry", "Customer opt-out", "Wrong number", "Other"] as const;
export type SuppressionEntry = { id: string; phone?: string | null; email?: string | null; reason: string; createdAt: string };
export type ComplianceMatch = { reason: string; createdAt: string };
